import Vue from 'vue'
import router from '@/router'
import createAuth0Client from '@auth0/auth0-spa-js'
import UserApi from '@/api/User'
import store from '@/store'

/** Define a default action to perform after authentication */
const DEFAULT_REDIRECT_CALLBACK = () => window.history.replaceState({}, document.title, window.location.pathname)

let instance

/** Returns the current instance of the SDK */
export const getInstance = () => instance

/** Creates an instance of the Auth0 SDK. If one has already been created, it returns that instance */
export const useAuth0 = ({
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  redirectUri = window.location.origin,
  ...options
}) => {
  if (instance) return instance

  // The 'instance' is simply a Vue object
  instance = new Vue({
    data() {
      return {
        loading: true,
        isAuthenticated: false,
        isVerified: false,
        user: {},
        auth0Client: null,
        popupOpen: false,
        error: null,
      }
    },
    computed: {
      isDemo() {
        return Boolean(window.location.host.match(/^demo-\d+\.kvass\.no$/))
      },
    },
    methods: {
      /** Authenticates the user using a popup window */
      async loginWithPopup(o) {
        this.popupOpen = true

        try {
          await this.auth0Client.loginWithPopup(o)
        } catch (e) {
          // eslint-disable-next-line
          console.error(e)
        } finally {
          this.popupOpen = false
        }

        this.user = await this.auth0Client.getUser()
        this.isAuthenticated = true
      },
      /** Handles the callback when logging in using a redirect */
      async handleRedirectCallback() {
        this.loading = true
        try {
          await this.auth0Client.handleRedirectCallback()
          this.user = await this.auth0Client.getUser()
          this.isAuthenticated = true
        } catch (e) {
          this.error = e
        } finally {
          this.loading = false
        }
      },
      /** Authenticates the user using the redirect method */
      loginWithRedirect(o) {
        return this.auth0Client.loginWithRedirect(o)
      },
      /** Returns all the claims present in the ID token */
      getIdTokenClaims(o) {
        return this.auth0Client.getIdTokenClaims(o)
      },
      /** Returns the access token. If the token is invalid or missing, a new one is retrieved */
      getTokenSilently(o) {
        return this.auth0Client.getTokenSilently(o)
      },
      /** Gets the access token using a popup window */

      getTokenWithPopup(o) {
        return this.auth0Client.getTokenWithPopup(o)
      },
      /** Logs the user out and removes their session on the authorization server */
      logout(o = {}) {
        if (!o.returnTo) o.returnTo = window.location.origin
        return this.auth0Client.logout(o)
      },
    },
    /** Use this lifecycle method to instantiate the SDK client */
    async created() {
      let appState
      let currentRoute = router.matcher.match(location.pathname)
      if (currentRoute && currentRoute.meta.skipAuth) {
        await store.dispatch('init')
        return (this.loading = false)
      }

      // Create a new instance of the SDK client using members of the given options object
      this.auth0Client = await createAuth0Client({
        domain: options.domain,
        client_id: options.clientId,
        audience: options.audience,
        redirect_uri: redirectUri,
      })

      let params = new URLSearchParams(window.location.search)

      if (params.get('error') === 'access_denied')
        return await this.logout({
          returnTo: window.location.protocol + '//' + window.location.host + '/401',
        })

      try {
        // If the user is returning to the app after authentication..
        if (params.has('code') && params.has('state')) {
          // handle the redirect and retrieve tokens
          appState = (await this.auth0Client.handleRedirectCallback()).appState

          // Notify subscribers that the redirect callback has happened, passing the appState
          // (useful for retrieving any pre-authentication state)
          onRedirectCallback(appState)
        }
      } catch (e) {
        this.error = e
      } finally {
        // Initialize our internal authentication state
        let [isAuthenticated, user] = await Promise.all([
          this.auth0Client.isAuthenticated(),
          this.auth0Client.getUser(),
          store.dispatch('init'),
        ])

        this.isAuthenticated = isAuthenticated
        this.user = user

        if (!this.user) return (this.loading = false)
        if (currentRoute && currentRoute.meta.skipVerification) return (this.loading = false)

        try {
          await UserApi.verify({ email: this.user.email })
          this.isVerified = true

          if (appState) {
            const redirect = new URLSearchParams(appState.targetUrl.split('?').pop()).get('redirect')
            if (redirect) return (window.location.href = redirect)
          }
        } catch (e) {
          let name
          let path
          if (e.status === 423) {
            name = 'account-agreement-awaiting'
            path = '/account-agreement/awaiting'
          }
          if (e.status === 412) {
            const data = await e.json()
            name = 'account-agreement-sign'
            path = '/account-agreement/sign'
            window.sessionStorage.setItem('accountAgreementData', JSON.stringify(data))
          }

          if (this.user.email.endsWith('@kvass.no')) {
            return (location.href = `/request-access?email=${this.user.email}&redirect=${currentRoute.fullPath}`)
          }

          if (name) {
            return (window.location.href = window.location.protocol + '//' + window.location.host + path)

            // TODO: Loading watcher makes this fail
            return router.push({
              name,
            })
          }

          this.error = e
          this.loading = false

          return this.logout({
            returnTo: window.location.protocol + '//' + window.location.host + '/401',
          })
        }

        this.loading = false
      }
    },
  })

  return instance
}

// Create a simple Vue plugin to expose the wrapper object throughout the application
export const Auth0Plugin = {
  install(Vue, options) {
    Vue.prototype.$auth = useAuth0(options)
  },
}
